import Checkbox from '@/components/Checkbox';
import { useState, useRef, useEffect } from 'react';

type CustomFieldOption = {
  id: number;
  value: string;
};

type Props = {
  customFieldOptions: CustomFieldOption[];
  onChange?: (selectedOptions: CustomFieldOption[]) => void;
  initialValues?: string;
};

export default function MultiSelect({ customFieldOptions, onChange, initialValues }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<CustomFieldOption[]>(() => {
    if (!initialValues) return [];
    const values = initialValues
      .split(/[,/]/)
      .map((v) => v.trim())
      .filter((v) => v);
    return customFieldOptions.filter((option) => values.includes(option.value));
  });
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleOption = (option: CustomFieldOption) => {
    const isSelected = selectedOptions.some((o) => o.id === option.id);
    let newSelectedOptions: CustomFieldOption[];

    if (isSelected) {
      newSelectedOptions = selectedOptions.filter((o) => o.id !== option.id);
    } else {
      newSelectedOptions = [...selectedOptions, option];
    }

    setSelectedOptions(newSelectedOptions);

    if (onChange) {
      onChange(newSelectedOptions);
    }
  };

  const removeOption = (optionId: number) => {
    const newSelectedOptions = selectedOptions.filter((o) => o.id !== optionId);
    setSelectedOptions(newSelectedOptions);

    if (onChange) {
      onChange(newSelectedOptions);
    }
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        className="flex h-[42px] w-full items-center rounded border border-solid border-border bg-white px-3"
        onClick={toggleDropdown}
      >
        <div className="hidden-scrollbar flex items-center gap-2 overflow-hidden overflow-x-scroll">
          {selectedOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              className="border-text/disabled flex h-lg items-center gap-1 rounded-full border border-solid border-border bg-white px-3 text-xs whitespace-pre"
              onClick={(e) => {
                e.stopPropagation();
                removeOption(option.id);
              }}
            >
              {option.value}
              <img
                src="/agent/candidates/icon-cancel.svg"
                width={20}
                height={20}
                alt=""
                className="opacity-50"
              />
            </button>
          ))}
        </div>
        <img
          src="/agent/candidates/icon-arrow-down-default.svg"
          width={20}
          height={20}
          alt=""
          className={`ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </div>
      {isOpen && (
        <div className="absolute top-[46px] z-50 w-full rounded bg-white shadow-[0px_0px_12px_0px_#00000033]">
          {customFieldOptions.map((option) => {
            const isSelected = selectedOptions.some((o) => o.id === option.id);
            return (
              <div
                key={option.id}
                className="hover:bg-background/gray flex h-xxl cursor-pointer items-center gap-2 px-3"
                onClick={(e) => {
                  if (e.target === e.currentTarget) toggleOption(option);
                }}
              >
                <Checkbox checked={isSelected} onChange={() => toggleOption(option)}>
                  <p className="text-sm">{option.value}</p>
                </Checkbox>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
